import { useState } from 'react';
import { mediaUrl } from '../mediaUrl';

interface AssetFile {
  [format: string]: string;
}

interface Asset {
  id: string;
  text: string;
  font: string;
  width_inches: number;
  height_inches: number;
  created_at: string;
  files: AssetFile;
  has_embroidery: boolean;
  is_unlocked: boolean;
}

interface PastAssetsGridProps {
  assets: Asset[];
  apiUrl: string;
  onSelectAsset: (asset: Asset) => void;
}

function AssetCard({ asset, apiUrl, onSelectAsset }: { asset: Asset; apiUrl: string; onSelectAsset: (asset: Asset) => void }) {
  const [imageError, setImageError] = useState(false);

  return (
    <div
      style={{
        border: '1px solid var(--pico-muted-border-color)',
        borderRadius: '0.5rem',
        padding: '0.5rem',
        cursor: 'pointer',
        position: 'relative',
      }}
      onClick={() => onSelectAsset(asset)}
    >
      {asset.is_unlocked && (
        <div
          title="Embroidery files ready"
          style={{
            position: 'absolute',
            // Pinned to the card's outer corner, not inset from it. A 28px
            // badge inset by 0.75rem landed in the middle of the right-hand
            // side of these thumbnails -- they are wide, short strips of
            // text, so "top right" and "center right" are nearly the same
            // place -- and covered the last letters of the design. Negative
            // offsets put it clear of the image box entirely.
            top: '-0.5rem',
            right: '-0.5rem',
            backgroundColor: 'var(--pico-primary-background)',
            borderRadius: '50%',
            width: '18px',
            height: '18px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '10px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            zIndex: 1,
          }}
        >
          🧵
        </div>
      )}
      {imageError ? (
        <div style={{
          width: '100%',
          aspectRatio: '3/2',
          backgroundColor: 'var(--pico-muted-border-color)',
          borderRadius: '0.25rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '2rem',
        }}>
          📄
        </div>
      ) : (
        <img
          src={mediaUrl(apiUrl, asset.files.png)}
          alt={asset.text}
          style={{ width: '100%', height: 'auto', borderRadius: '0.25rem' }}
          onError={() => setImageError(true)}
        />
      )}
      <small style={{ display: 'block', marginTop: '0.5rem', textAlign: 'center' }}>
        {asset.text.length > 20 ? asset.text.substring(0, 20) + '...' : asset.text}
      </small>
    </div>
  );
}

export function PastAssetsGrid({ assets, apiUrl, onSelectAsset }: PastAssetsGridProps) {
  if (assets.length === 0) {
    return null;
  }

  return (
    <section className="past-designs">
      <h3>Past Designs</h3>
      <p>Select a previous design to download prior embroidery files.</p>
      <div className="past-designs-grid">
        {assets.map((asset) => (
          <AssetCard
            key={asset.id}
            asset={asset}
            apiUrl={apiUrl}
            onSelectAsset={onSelectAsset}
          />
        ))}
      </div>
    </section>
  );
}
